import { CardProps, RowConfig } from '@cdaas/reusable-components-daas';
import { Tag } from '@gaiads/telia-react-component-library';
import { DateTime } from 'luxon';

import { User } from '../../../types';
import { DATE_FORMAT } from '../../../utils/constants';

export enum ErrorTypes {
  PhoneNumberMissing,
  EmailMissing,
  PhoneNumberInvalid,
  EmailInvalid,
  PhoneNumberDuplicateBetweenToBeImportedUsers, // Two users to be imported have same phone number
  EmailDuplicateBetweenToBeImportedUsers, // Two users to be imported have same email
  PhoneNumberDuplicateBetweenExistingUser, // User to be imported has same phone number with some user already existing in the system
  EmailDuplicateBetweenExistingUser, // User to be imported has same email with some user already existing in the system
  NonExistingUserGroup, // User is in a user group that does not exist in the system. Perhaps old excel template was used with user groups since deleted
  PreScheduledOnboardingAtInvalid, // Invalid pre-scheduled onboarding date
  PreScheduledOnboardingAtInPast, // Pre-scheduled onboarding date is in the past
}

export interface UserWithPossibleError extends User {
  error?: {
    errorType: ErrorTypes;
    errorMessage: string;
  };
}

const formatEmailColumn = (email: string, user: UserWithPossibleError) => {
  if (
    user.error?.errorType === ErrorTypes.EmailMissing ||
    user.error?.errorType === ErrorTypes.EmailInvalid ||
    user.error?.errorType === ErrorTypes.EmailDuplicateBetweenToBeImportedUsers ||
    user.error?.errorType === ErrorTypes.EmailDuplicateBetweenExistingUser
  ) {
    return <Tag color="red600">{user.error.errorMessage}</Tag>;
  }
  return email;
};

const formatPhoneNumberColumn = (phoneNumber: string, user: UserWithPossibleError) => {
  if (
    user.error?.errorType === ErrorTypes.PhoneNumberMissing ||
    user.error?.errorType === ErrorTypes.PhoneNumberInvalid ||
    user.error?.errorType === ErrorTypes.PhoneNumberDuplicateBetweenToBeImportedUsers ||
    user.error?.errorType === ErrorTypes.PhoneNumberDuplicateBetweenExistingUser
  ) {
    return <Tag color="red600">{user.error.errorMessage}</Tag>;
  }
  return phoneNumber;
};

const formatPreScheduledOnboardingAtColumn = (preScheduledOnboardingAt: string | null, user: UserWithPossibleError) => {
  if (user.error?.errorType === ErrorTypes.PreScheduledOnboardingAtInvalid || user.error?.errorType === ErrorTypes.PreScheduledOnboardingAtInPast) {
    return <Tag color="red600">{user.error.errorMessage}</Tag>;
  }
  if (!preScheduledOnboardingAt) return '';
  const date = new Date(preScheduledOnboardingAt);
  if (Number.isNaN(date.getTime())) return '';
  return DateTime.fromISO(preScheduledOnboardingAt).toFormat(DATE_FORMAT);
};

export const getCardProps = (
  group: { userGroup: string; users: UserWithPossibleError[] },
  t: (key: string, options?: Record<string, unknown>) => string,
): CardProps => ({
  title: group.userGroup,
  infoText: { text: t('usersAdmin.importUsers.reviewAndSave.groupUserCount', { count: group.users.length }) },
  wrapperDivCustomProps: { backgroundColor: 'white', margin: { bottom: 'sm' } },
});

export const getRowConfig = (): RowConfig<UserWithPossibleError>[] => [
  { key: 'email', formatValue: (value, row) => formatEmailColumn(value, row), cellWidthMultiplier: 2 },
  { key: 'phone_number', formatValue: (value, row) => formatPhoneNumberColumn(value, row), cellWidthMultiplier: 2 },
  { key: 'employee_id', cellWidthMultiplier: 1 },
  {
    key: 'prescheduled_onboarding_at',
    formatValue: (value, row) => formatPreScheduledOnboardingAtColumn(value, row),
    cellWidthMultiplier: 1,
  },
];

export type UsersByGroup = { userGroup: string; userGroupId?: string; users: UserWithPossibleError[] };

export const groupUsersByGroup = (users: User[], userWithoutGroup: User[]): UsersByGroup[] => {
  return users.reduce((acc: UsersByGroup[], user: User) => {
    if (!user.user_group?.name) {
      userWithoutGroup.push(user);
      return acc;
    }
    const groupIndex = acc.findIndex((group: UsersByGroup) => group.userGroup === user.user_group?.name);
    if (groupIndex === -1) {
      acc.push({ userGroup: user.user_group.name, userGroupId: user.user_group?.id, users: [user] });
    } else {
      acc[groupIndex].users.push(user);
    }
    return acc;
  }, []);
};
